/**
 * EQUICORE THEME — equicore-theme.js
 * Vanilla JS — no jQuery or large external libraries
 */

(function () {
  'use strict';

  // ============================================================
  // Utility helpers
  // ============================================================
  function qs(selector, parent) {
    return (parent || document).querySelector(selector);
  }
  function qsa(selector, parent) {
    return Array.from((parent || document).querySelectorAll(selector));
  }
  function on(el, event, handler, options) {
    if (el) el.addEventListener(event, handler, options);
  }
  function lockBody() {
    document.body.classList.add('equicore-body-locked');
  }
  function unlockBody() {
    document.body.classList.remove('equicore-body-locked');
  }
  function trapFocus(container) {
    var focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    function handleTab(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    on(container, 'keydown', handleTab);
    return handleTab;
  }

  function productOptionCount(product) {
    return product.options
      ? product.options.length
      : ((product.variants[0] && product.variants[0].options.length) || 0);
  }

  function selectionIsComplete(product, selectedOptions) {
    var optionCount = productOptionCount(product);
    if (!optionCount) return false;
    for (var position = 1; position <= optionCount; position += 1) {
      if (typeof selectedOptions[String(position)] === 'undefined') return false;
    }
    return true;
  }

  function findVariantForSelection(product, selectedOptions) {
    var optionCount = productOptionCount(product);
    if (!selectionIsComplete(product, selectedOptions)) return null;
    return product.variants.find(function (variant) {
      for (var position = 1; position <= optionCount; position += 1) {
        if (variant.options[position - 1] !== selectedOptions[String(position)]) return false;
      }
      return true;
    }) || null;
  }

  function optionValueIsAvailable(product, selectedOptions, position, value) {
    var numericPosition = parseInt(position, 10);
    var optionIndex = numericPosition - 1;
    return product.variants.some(function (variant) {
      if (!variant.available || variant.options[optionIndex] !== value) return false;
      return Object.keys(selectedOptions).every(function (selectedPosition) {
        if (parseInt(selectedPosition, 10) >= numericPosition) return true;
        return variant.options[parseInt(selectedPosition, 10) - 1] === selectedOptions[selectedPosition];
      });
    });
  }

  window.EquicoreVariantSelection = Object.freeze({
    findVariant: findVariantForSelection,
    isOptionValueAvailable: optionValueIsAvailable
  });

  // ============================================================
  // Announcement Bar — close / session storage
  // ============================================================
  (function initAnnouncementBar() {
    var bar = qs('#equicore-announcement-bar');
    if (!bar) return;
    var closeBtn = qs('[data-announcement-close]', bar);
    if (!closeBtn) return;
    var storageKey = 'equicore-announcement-closed';
    if (sessionStorage.getItem(storageKey)) {
      bar.classList.add('equicore-announcement-bar--hidden');
      return;
    }
    on(closeBtn, 'click', function () {
      bar.classList.add('equicore-announcement-bar--hidden');
      sessionStorage.setItem(storageKey, '1');
    });
  })();

  // ============================================================
  // Header — search toggle
  // ============================================================
  (function initSearch() {
    var searchBar = qs('#equicore-search');
    if (!searchBar) return;
    qsa('[data-search-toggle]').forEach(function (btn) {
      on(btn, 'click', function () {
        var hidden = searchBar.getAttribute('aria-hidden') === 'true';
        searchBar.setAttribute('aria-hidden', hidden ? 'false' : 'true');
        if (!hidden) {
          searchBar.querySelector('input') && searchBar.querySelector('input').focus();
        }
      });
    });
    qsa('[data-search-close]').forEach(function (btn) {
      on(btn, 'click', function () {
        searchBar.setAttribute('aria-hidden', 'true');
      });
    });
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && searchBar.getAttribute('aria-hidden') === 'false') {
        searchBar.setAttribute('aria-hidden', 'true');
      }
    });
  })();

  // ============================================================
  // Mobile menu
  // ============================================================
  (function initMobileMenu() {
    var mobileNav = qs('#equicore-mobile-nav');
    if (!mobileNav) return;
    var toggleBtns = qsa('[data-mobile-menu-toggle]');
    var closeBtn = qs('[data-mobile-menu-close]', mobileNav);
    var overlay = qs('[data-mobile-menu-overlay]', mobileNav);
    var trapHandler = null;

    function openMenu() {
      mobileNav.setAttribute('aria-hidden', 'false');
      toggleBtns.forEach(function (btn) { btn.setAttribute('aria-expanded', 'true'); });
      lockBody();
      trapHandler = trapFocus(mobileNav);
      closeBtn && closeBtn.focus();
    }
    function closeMenu() {
      mobileNav.setAttribute('aria-hidden', 'true');
      toggleBtns.forEach(function (btn) { btn.setAttribute('aria-expanded', 'false'); });
      unlockBody();
      if (trapHandler) { mobileNav.removeEventListener('keydown', trapHandler); trapHandler = null; }
    }

    toggleBtns.forEach(function (btn) { on(btn, 'click', openMenu); });
    on(closeBtn, 'click', closeMenu);
    on(overlay, 'click', closeMenu);
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && mobileNav.getAttribute('aria-hidden') === 'false') closeMenu();
    });
  })();

  // ============================================================
  // Sticky header — add shadow on scroll
  // ============================================================
  (function initStickyHeader() {
    var header = qs('#equicore-header');
    if (!header) return;
    function onScroll() {
      header.classList.toggle('equicore-header--scrolled', window.scrollY > 20);
    }
    on(window, 'scroll', onScroll, { passive: true });
    onScroll();
  })();

  // ============================================================
  // Cart Drawer
  // ──────────────────────────────────────────────────────────
  // TWO INSTALLATION SCENARIOS
  //
  // A) Content-section-only (recommended for existing Dawn stores)
  //    Dawn's own header, footer, and cart-drawer.liquid remain active.
  //    Equicore's cart-drawer.liquid is NOT rendered.
  //    A <cart-drawer> custom element IS present in the DOM.
  //    equicore-theme.js delegates to it — no #equicore-cart-drawer needed.
  //
  // B) Full Equicore header on Dawn
  //    Equicore's header.liquid and cart-drawer.liquid replace Dawn's.
  //    Equicore's #equicore-cart-drawer IS present; no <cart-drawer> element.
  //    equicore-theme.js runs its own standalone implementation.
  //
  // Dawn mode detection runs at deferred-script execution time using
  // customElements.get(), NOT from the inline snippet flag — that inline
  // script runs during HTML parsing before deferred scripts, so
  // customElements.get('cart-drawer') is always null there.
  // ============================================================
  var CartDrawer = (function () {
    // ── Step 1: Dawn detection (MUST run before the early return) ──
    // customElements.get() is reliable here because equicore-theme.js is
    // deferred; Dawn's cart-drawer.js (also deferred, earlier in theme.liquid)
    // has already registered the element by this point.
    var dawnClassRegistered = typeof customElements !== 'undefined' &&
                              customElements.get('cart-drawer') != null;
    // Dawn delegation only applies when BOTH the class is registered AND a
    // <cart-drawer> DOM instance exists (Scenario A).  If Dawn's JS is loaded
    // but its HTML snippet was replaced by Equicore's, the element is absent —
    // fall through to Scenario B (Equicore standalone).
    var dawnEl = dawnClassRegistered ? document.querySelector('cart-drawer') : null;
    var useDawnDelegation = dawnClassRegistered && dawnEl != null;

    // Expose so initAddToCart() and external code can read the resolved mode.
    window.equicoreCartDrawerMode = useDawnDelegation ? 'dawn' : 'equicore';

    // --- Dawn delegation path (Scenario A) ----------------------
    if (useDawnDelegation) {
      // dawnEl is a live <cart-drawer> element upgraded by Dawn's cart-drawer.js.

      // ── Dawn v6 / v7 / v8 — verified API surface ─────────────────
      // Confirmed against Dawn v6.0.0, v7.0.0, v8.0.0 source.
      // All three versions expose an identical CartDrawer class:
      //
      //   open(triggeredBy?)
      //     Display-only: adds CSS class 'animate active', traps focus.
      //     Does NOT fetch or render cart contents.
      //
      //   close()
      //     Removes 'active' class, releases focus trap.
      //
      //   getSectionsToRender()
      //     Returns [{id, selector?}, …].  The .id values are passed as
      //     the 'sections' field in cart mutation POST bodies so Shopify
      //     returns rendered section HTML in the response.
      //
      //   renderContents(parsedState)
      //     The authoritative render path, identical to Dawn's own
      //     product-form.js.  Reads parsedState.sections[section.id]
      //     for each entry from getSectionsToRender() and sets the
      //     matching DOM element's innerHTML.  Calls open() internally.
      //     parsedState is the full /cart/add.js response object, which
      //     includes a .sections key when section IDs were supplied in
      //     the POST body.
      //
      // No version discriminator is needed — the API is identical
      // across v6, v7, and v8.

      function openDawnDrawer(triggeredBy) {
        if (!dawnEl) return;
        if (typeof dawnEl.open === 'function') {
          dawnEl.open(triggeredBy || null);
        } else {
          // Element registered but not yet upgraded (script-load race).
          // aria-expanded alone does not trigger renderContents — use
          // only as a last resort.
          dawnEl.setAttribute('aria-expanded', 'true');
        }
      }

      function closeDawnDrawer() {
        if (!dawnEl) return;
        if (typeof dawnEl.close === 'function') {
          dawnEl.close();
        } else {
          dawnEl.setAttribute('aria-expanded', 'false');
        }
      }

      // Wire equicore [data-cart-toggle] triggers to Dawn's drawer.
      // Pass the clicked element so Dawn restores focus to it on close.
      qsa('[data-cart-toggle]').forEach(function (btn) {
        on(btn, 'click', function (e) { openDawnDrawer(e.currentTarget); });
      });

      return {
        openDrawer: openDawnDrawer,
        closeDrawer: closeDawnDrawer,
        fetchCart: function () {
          return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
            .then(function (r) { return r.json(); });
        },

        // Returns the section IDs that initAddToCart() must append to the
        // /cart/add.js FormData as 'sections'.  Mirrors product-form.js:
        //   formData.append('sections', cart.getSectionsToRender().map(s=>s.id))
        getDawnSectionIds: function () {
          if (!dawnEl || typeof dawnEl.getSectionsToRender !== 'function') {
            return ['cart-drawer', 'cart-icon-bubble'];
          }
          return dawnEl.getSectionsToRender().map(function (s) { return s.id; });
        },

        // Called by initAddToCart() with the raw /cart/add.js response.
        // parsedState.sections[id] contains rendered HTML because
        // initAddToCart() appended section IDs to the FormData before POST.
        // renderContents() updates drawer + cart-icon-bubble, then opens.
        renderDawnContents: function (parsedState) {
          if (parsedState && parsedState.sections &&
              typeof dawnEl.renderContents === 'function') {
            try {
              dawnEl.renderContents(parsedState);
            } catch (e) {
              // renderContents may throw if Dawn's expected DOM elements
              // (#CartDrawer, #cart-icon-bubble) are absent when Equicore's
              // header.liquid is used instead of Dawn's.  Fall back to
              // display-only open().
              openDawnDrawer(null);
            }
          } else {
            // sections absent from response — just open the drawer as-is.
            openDawnDrawer(null);
          }
          // Update the Equicore header badge (present when Equicore's
          // header.liquid is used alongside Dawn's content sections).
          // The /cart/add.js response is the added item, not full cart,
          // so fetch item_count separately only if the badge is present.
          var cartCountEl = document.querySelector('.equicore-header__cart-count');
          if (cartCountEl) {
            fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
              .then(function (r) { return r.json(); })
              .then(function (cart) {
                cartCountEl.textContent = cart.item_count;
                cartCountEl.style.display = cart.item_count > 0 ? '' : 'none';
              });
          }
        },

        updateCartUI: function (cart) {
          // updateCartUI in Dawn mode is used for non-add-to-cart scenarios
          // (e.g. if called externally).  Dawn manages its own drawer render;
          // update only the Equicore header badge if present.
          var cartCountEl = document.querySelector('.equicore-header__cart-count');
          if (cartCountEl) {
            cartCountEl.textContent = cart.item_count;
            cartCountEl.style.display = cart.item_count > 0 ? '' : 'none';
          }
        }
      };
    }
    // --- End Dawn delegation path --------------------------------

    // ── Scenario B / standalone: need Equicore's own drawer ──────
    // If Dawn's class is registered but no <cart-drawer> DOM element
    // exists (Equicore's snippet replaced it), or if Dawn is absent
    // entirely, fall through to the Equicore standalone implementation.
    var drawer = qs('#equicore-cart-drawer');
    if (!drawer) return {};

    var itemsContainer = qs('#cart-drawer-items', drawer);
    var countEls = qsa('[data-cart-count]');
    var subtotalEls = qsa('[data-cart-subtotal]');
    var loadingEl = qs('#cart-drawer-loading', drawer);
    var cartCountHeaderEl = qs('.equicore-header__cart-count');
    var freeShippingThreshold = window.freeShippingThreshold || 0;

    function openDrawer() {
      drawer.setAttribute('aria-hidden', 'false');
      lockBody();
      var closeBtn = qs('[data-cart-close]', drawer);
      if (closeBtn) closeBtn.focus();
    }
    function closeDrawer() {
      drawer.setAttribute('aria-hidden', 'true');
      unlockBody();
    }

    function showLoading() { if (loadingEl) loadingEl.setAttribute('aria-hidden', 'false'); }
    function hideLoading() { if (loadingEl) loadingEl.setAttribute('aria-hidden', 'true'); }

    function formatMoney(cents) {
      var dollars = (cents / 100).toFixed(2);
      return '$' + dollars + ' AUD';
    }

    function updateShippingBar(cartTotal) {
      var msgEl = qs('[data-shipping-msg]', drawer);
      var fillEl = qs('[data-shipping-fill]', drawer);
      if (!msgEl || !fillEl || freeShippingThreshold === 0) return;
      var remaining = freeShippingThreshold - cartTotal;
      var pct = Math.min((cartTotal / freeShippingThreshold) * 100, 100);
      fillEl.style.width = pct + '%';
      if (remaining > 0) {
        msgEl.textContent = 'Spend ' + formatMoney(remaining) + ' more for free shipping';
        msgEl.classList.remove('equicore-cart-drawer__shipping-msg--achieved');
      } else {
        msgEl.textContent = '🎉 You\'ve unlocked free shipping!';
        msgEl.classList.add('equicore-cart-drawer__shipping-msg--achieved');
      }
    }

    function fetchCart() {
      return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } }).then(function (r) { return r.json(); });
    }

    function renderItems(cart) {
      if (!itemsContainer) return;
      if (cart.item_count === 0) {
        itemsContainer.innerHTML = '<div class="equicore-cart-drawer__empty" data-cart-empty>' +
          '<p class="equicore-cart-drawer__empty-msg">Your cart is empty.</p>' +
          '<a href="/collections/all" class="equicore-btn equicore-btn--dark">Shop All</a>' +
          '</div>';
        return;
      }

      var html = cart.items.map(function (item) {
        var imgHtml = item.featured_image && item.featured_image.url
          ? '<img src="' + item.featured_image.url + '&width=160" alt="' + item.title.replace(/"/g, '&quot;') + '" width="80" height="100" loading="lazy" class="equicore-cart-item__img">'
          : '';
        var variantTitle = item.variant_title !== 'Default Title' ? '<p class="equicore-cart-item__variant">' + item.variant_title + '</p>' : '';
        return '<div class="equicore-cart-item" data-cart-item data-item-key="' + item.key + '">' +
          '<div class="equicore-cart-item__img-link">' + imgHtml + '</div>' +
          '<div class="equicore-cart-item__details">' +
            '<p class="equicore-cart-item__vendor">' + item.vendor + '</p>' +
            '<a href="' + item.url + '" class="equicore-cart-item__title">' + item.product_title + '</a>' +
            variantTitle +
            '<p class="equicore-cart-item__price">' + formatMoney(item.final_line_price) + '</p>' +
            '<div class="equicore-cart-item__qty-row">' +
              '<div class="equicore-cart-item__qty">' +
                '<button type="button" class="equicore-cart-item__qty-btn" aria-label="Decrease quantity" data-item-qty-minus data-item-key="' + item.key + '">−</button>' +
                '<span class="equicore-cart-item__qty-val">' + item.quantity + '</span>' +
                '<button type="button" class="equicore-cart-item__qty-btn" aria-label="Increase quantity" data-item-qty-plus data-item-key="' + item.key + '">+</button>' +
              '</div>' +
              '<button type="button" class="equicore-cart-item__remove" data-item-remove data-item-key="' + item.key + '">Remove</button>' +
            '</div>' +
          '</div>' +
        '</div>';
      }).join('');
      itemsContainer.innerHTML = html;
    }

    function updateCartUI(cart) {
      renderItems(cart);
      countEls.forEach(function (el) { el.textContent = cart.item_count; });
      if (cartCountHeaderEl) {
        cartCountHeaderEl.textContent = cart.item_count;
        cartCountHeaderEl.style.display = cart.item_count > 0 ? '' : 'none';
      }
      subtotalEls.forEach(function (el) { el.textContent = formatMoney(cart.total_price); });
      updateShippingBar(cart.total_price);

      // Re-bind item events after re-render
      bindItemEvents();
    }

    function changeItemQty(key, delta) {
      showLoading();
      fetchCart().then(function (cart) {
        var item = cart.items.find(function (i) { return i.key === key; });
        if (!item) { hideLoading(); return; }
        var newQty = Math.max(0, item.quantity + delta);
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: newQty })
        }).then(function (r) { return r.json(); });
      }).then(function (cart) {
        if (cart) updateCartUI(cart);
        hideLoading();
      }).catch(function () { hideLoading(); });
    }

    function removeItem(key) {
      showLoading();
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      }).then(function (r) { return r.json(); })
        .then(function (cart) { updateCartUI(cart); hideLoading(); })
        .catch(function () { hideLoading(); });
    }

    function bindItemEvents() {
      qsa('[data-item-qty-minus]', drawer).forEach(function (btn) {
        on(btn, 'click', function () { changeItemQty(btn.dataset.itemKey, -1); });
      });
      qsa('[data-item-qty-plus]', drawer).forEach(function (btn) {
        on(btn, 'click', function () { changeItemQty(btn.dataset.itemKey, 1); });
      });
      qsa('[data-item-remove]', drawer).forEach(function (btn) {
        on(btn, 'click', function () { removeItem(btn.dataset.itemKey); });
      });
    }

    // Close buttons / overlay
    qsa('[data-cart-close]', drawer).forEach(function (btn) { on(btn, 'click', closeDrawer); });
    on(qs('[data-cart-overlay]', drawer), 'click', closeDrawer);
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') closeDrawer();
    });

    // Open cart
    qsa('[data-cart-toggle]').forEach(function (btn) { on(btn, 'click', openDrawer); });

    bindItemEvents();

    return { openDrawer: openDrawer, closeDrawer: closeDrawer, fetchCart: fetchCart, updateCartUI: updateCartUI };
  })();

  // ============================================================
  // Add-to-cart intercept (cart drawer)
  // ============================================================
  (function initAddToCart() {
    on(document, 'submit', function (e) {
      var form = e.target.closest('form[action="/cart/add"]') || (e.target.action && e.target.action.includes('/cart/add') ? e.target : null);
      if (!form) return;
      if (!window.routes || !CartDrawer.openDrawer) return;

      // On Dawn stores (Scenario A — content-section-only), Dawn's own
      // product-form.js attaches a listener directly on the form element.
      // That listener fires first (form-level before document-level bubbling),
      // calls e.preventDefault(), and posts to /cart/add.js itself.
      // By the time this document-level listener fires, defaultPrevented is
      // already true — yielding here prevents a duplicate POST and a double
      // drawer render.
      if (e.defaultPrevented) return;

      e.preventDefault();

      var formData = new FormData(form);
      var submitBtn = form.querySelector('[name="add"]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Adding…';
      }

      // ── Dawn mode: bundle section IDs with the POST ────────────
      // Mirrors Dawn's own product-form.js exactly:
      //   formData.append('sections', cart.getSectionsToRender().map(s=>s.id))
      //   formData.append('sections_url', window.location.pathname)
      // /cart/add.js then returns section HTML in response.sections[id],
      // which renderDawnContents() passes directly to renderContents().
      var isDawn = window.equicoreCartDrawerMode === 'dawn';
      if (isDawn && CartDrawer.getDawnSectionIds) {
        formData.append('sections', CartDrawer.getDawnSectionIds());
        formData.append('sections_url', window.location.pathname);
      }

      fetch('/cart/add.js', { method: 'POST', body: formData })
        .then(function (r) { return r.json(); })
        .then(function (parsedState) {
          if (isDawn && CartDrawer.renderDawnContents) {
            // parsedState.sections has rendered section HTML from /cart/add.js.
            // renderDawnContents() calls renderContents() → renders + opens.
            CartDrawer.renderDawnContents(parsedState);
            if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'ADD TO CART'; }
          } else {
            return CartDrawer.fetchCart();
          }
        })
        .then(function (cart) {
          if (cart) { // undefined in Dawn path
            CartDrawer.updateCartUI(cart);
            CartDrawer.openDrawer();
            if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'ADD TO CART'; }
          }
        })
        .catch(function () {
          if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'ADD TO CART'; }
          form.submit();
        });
    });
  })();

  // ============================================================
  // Product Gallery — thumbnail switcher
  // ============================================================
  (function initProductGallery() {
    qsa('[data-thumb-btn]').forEach(function (btn) {
      on(btn, 'click', function () {
        var mediaId = btn.dataset.mediaId;
        var section = btn.closest('[data-section-id]');
        if (!section) return;

        qsa('[data-thumb-btn]', section).forEach(function (b) { b.classList.remove('equicore-product__thumb--active'); });
        btn.classList.add('equicore-product__thumb--active');

        qsa('.equicore-product__media-item', section).forEach(function (item) {
          item.classList.toggle('equicore-product__media-item--active', item.dataset.mediaId === mediaId);
        });
      });
    });
  })();

  // ============================================================
  // Product variant selector
  // ============================================================
  (function initVariantSelector() {
    qsa('[data-section-id]').forEach(function (section) {
      var productJsonEl = document.getElementById('product-json-' + section.dataset.sectionId);
      if (!productJsonEl) return;

      var product;
      try { product = JSON.parse(productJsonEl.textContent); } catch (e) { return; }

      var variantInput = qs('[data-product-variant-id]', section);
      var priceWrap = qs('[id^="price-"]', section);
      var addBtn = qs('#add-to-cart-' + section.dataset.sectionId, section) || qs('[name="add"]', section);
      var stickyAddBtn = qs('[data-sticky-add-btn]', section);
      var stickyPrice = qs('[data-sticky-price]', section);
      var productForm = variantInput ? variantInput.closest('form') : null;

      var selectedOptions = {};
      var initialVariantId = variantInput ? String(variantInput.value) : '';
      var initialVariant = product.variants.find(function (variant) {
        return String(variant.id) === initialVariantId;
      });

      if (initialVariant) {
        initialVariant.options.forEach(function (value, index) {
          selectedOptions[String(index + 1)] = value;
        });
      }

      // Overlay the visible controls so every selected option uses its actual position.
      qsa('.equicore-product__size-btn--active', section).forEach(function (btn) {
        selectedOptions[btn.dataset.optionPosition] = btn.dataset.value;
      });
      qsa('[data-product-swatch].equicore-swatches__btn--active', section).forEach(function (btn) {
        selectedOptions[btn.dataset.optionPosition] = btn.dataset.swatch;
      });

      function findVariant() {
        return findVariantForSelection(product, selectedOptions);
      }

      function updateOptionAvailability() {
        qsa('.equicore-product__size-btn[data-option-position]', section).forEach(function (btn) {
          var available = optionValueIsAvailable(product, selectedOptions, btn.dataset.optionPosition, btn.dataset.value);
          btn.classList.toggle('equicore-product__size-btn--unavailable', !available);
          btn.setAttribute('aria-pressed', btn.classList.contains('equicore-product__size-btn--active') ? 'true' : 'false');
          if (available) {
            btn.removeAttribute('aria-label');
          } else {
            btn.setAttribute('aria-label', btn.dataset.value + ' — sold out');
          }
        });

        qsa('[data-product-swatch][data-option-position]', section).forEach(function (btn) {
          var available = optionValueIsAvailable(product, selectedOptions, btn.dataset.optionPosition, btn.dataset.swatch);
          btn.classList.toggle('equicore-swatches__btn--sold-out', !available);
          if (available) {
            btn.setAttribute('aria-label', btn.dataset.swatch);
          } else {
            btn.setAttribute('aria-label', btn.dataset.swatch + ' — sold out');
          }
        });
      }

      function updateVariantUI(variant) {
        if (!variant) {
          if (variantInput) variantInput.value = '';
          if (addBtn) {
            addBtn.disabled = true;
            addBtn.textContent = 'UNAVAILABLE';
          }
          if (stickyAddBtn) {
            stickyAddBtn.disabled = true;
            stickyAddBtn.textContent = 'UNAVAILABLE';
          }
          return;
        }
        if (variantInput) variantInput.value = variant.id;

        // Price
        if (priceWrap) {
          var saleHtml = variant.compare_at_price && variant.compare_at_price > variant.price
            ? '<span class="equicore-product__price equicore-product__price--sale">' + formatMoney(variant.price) + '</span><s class="equicore-product__price equicore-product__price--compare">' + formatMoney(variant.compare_at_price) + '</s>'
            : '<span class="equicore-product__price">' + formatMoney(variant.price) + '</span>';
          priceWrap.innerHTML = saleHtml;
        }

        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

        // Add to cart button
        if (addBtn) {
          addBtn.disabled = !variant.available;
          addBtn.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
        }
        if (stickyAddBtn) {
          stickyAddBtn.disabled = !variant.available;
          stickyAddBtn.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
        }

        // Update URL
        if (history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url.toString());
        }

        // Update featured image
        if (variant.featured_image) {
          var mediaId = '' + variant.featured_image.id;
          var thumbBtn = qs('[data-media-id="' + mediaId + '"]', section);
          if (thumbBtn) thumbBtn.click();
        }
      }

      function formatMoney(cents) {
        return '$' + (cents / 100).toFixed(2) + ' AUD';
      }

      // Size buttons
      on(section, 'click', function (e) {
        var btn = e.target.closest('.equicore-product__size-btn');
        if (!btn) return;
        var pos = btn.dataset.optionPosition;
        var val = btn.dataset.value;
        qsa('.equicore-product__size-btn[data-option-position="' + pos + '"]', section).forEach(function (b) {
          b.classList.remove('equicore-product__size-btn--active');
        });
        btn.classList.add('equicore-product__size-btn--active');
        selectedOptions[pos] = val;
        updateOptionAvailability();
        updateVariantUI(findVariant());
      });

      // Product swatches
      on(section, 'click', function (e) {
        var btn = e.target.closest('[data-product-swatch]');
        if (!btn) return;
        var pos = btn.dataset.optionPosition;
        var val = btn.dataset.swatch;
        qsa('[data-product-swatch][data-option-position="' + pos + '"]', section).forEach(function (b) {
          b.setAttribute('aria-pressed', 'false');
          b.classList.remove('equicore-swatches__btn--active');
        });
        btn.setAttribute('aria-pressed', 'true');
        btn.classList.add('equicore-swatches__btn--active');
        selectedOptions[pos] = val;
        // Update selected color display
        var colorDisplay = qs('[data-selected-color]', section);
        if (colorDisplay) colorDisplay.textContent = val;
        updateOptionAvailability();
        updateVariantUI(findVariant());
      });

      if (productForm) {
        on(productForm, 'submit', function (event) {
          if (!variantInput || !variantInput.value || !findVariant()) {
            event.preventDefault();
          }
        });
      }

      updateOptionAvailability();

      // Sticky cart trigger
      if (addBtn && stickyAddBtn) {
        var stickyCart = qs('[id^="sticky-cart-"]', section);
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (stickyCart) stickyCart.classList.toggle('equicore-product__sticky-cart--visible', !entry.isIntersecting);
          });
        }, { threshold: 0 });
        observer.observe(addBtn);

        on(stickyAddBtn, 'click', function () {
          if (addBtn) addBtn.click();
        });
      }
    });
  })();

  // ============================================================
  // Card swatches — swap image on card
  // ============================================================
  (function initCardSwatches() {
    on(document, 'click', function (e) {
      var btn = e.target.closest('[data-card-swatch]');
      if (!btn) return;
      var card = btn.closest('.equicore-product-card');
      if (!card) return;

      // Update swatch active state
      var container = btn.closest('.equicore-swatches');
      if (container) {
        qsa('.equicore-swatches__btn', container).forEach(function (b) {
          b.classList.remove('equicore-swatches__btn--active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('equicore-swatches__btn--active');
        btn.setAttribute('aria-pressed', 'true');
      }

      // Swap primary image
      var imgSrc = btn.dataset.imageSrc;
      if (imgSrc && imgSrc !== 'null') {
        var primaryImg = qs('[data-primary-image]', card);
        if (primaryImg) primaryImg.src = imgSrc;
      }
    });
  })();

  // ============================================================
  // Filter drawer (collection page)
  // ============================================================
  (function initFilterDrawer() {
    var drawer = qs('.equicore-filter-drawer');
    if (!drawer) return;

    var toggleBtns = qsa('[aria-controls="equicore-filter-drawer"]');
    var closeBtn = qs('[data-filter-close]', drawer);
    var overlay = qs('[data-filter-overlay]', drawer);
    var form = qs('#filter-form', drawer);

    function openDrawer() {
      drawer.setAttribute('aria-hidden', 'false');
      lockBody();
      toggleBtns.forEach(function (btn) { btn.setAttribute('aria-expanded', 'true'); });
    }
    function closeDrawer() {
      drawer.setAttribute('aria-hidden', 'true');
      unlockBody();
      toggleBtns.forEach(function (btn) { btn.setAttribute('aria-expanded', 'false'); });
    }

    toggleBtns.forEach(function (btn) { on(btn, 'click', openDrawer); });
    on(closeBtn, 'click', closeDrawer);
    on(overlay, 'click', closeDrawer);
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape') closeDrawer();
    });

    // Sort select
    var sortSelect = qs('[data-sort-select]');
    if (sortSelect) {
      on(sortSelect, 'change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortSelect.value);
        window.location.href = url.toString();
      });
    }
  })();

  // ============================================================
  // Size Guide Modal
  // ============================================================
  (function initSizeGuide() {
    var modal = qs('#size-guide-modal');
    if (!modal) return;

    var overlayEl = qs('[data-modal-overlay]', modal);
    var closeBtn = qs('[data-modal-close]', modal);
    var toggleBtns = qsa('[data-size-guide-toggle]');

    function openModal() {
      modal.setAttribute('aria-hidden', 'false');
      lockBody();
      closeBtn && closeBtn.focus();
    }
    function closeModal() {
      modal.setAttribute('aria-hidden', 'true');
      unlockBody();
    }

    toggleBtns.forEach(function (btn) { on(btn, 'click', openModal); });
    on(overlayEl, 'click', closeModal);
    on(closeBtn, 'click', closeModal);
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') closeModal();
    });
  })();

  // ============================================================
  // Quantity input (product page)
  // ============================================================
  (function initQuantityInputs() {
    on(document, 'click', function (e) {
      var btn = e.target.closest('[data-qty-minus], [data-qty-plus]');
      if (!btn) return;
      var wrap = btn.closest('.equicore-product__quantity-wrap');
      if (!wrap) return;
      var input = wrap.querySelector('.equicore-product__qty-input');
      if (!input) return;
      var current = parseInt(input.value, 10) || 1;
      var isPlus = btn.hasAttribute('data-qty-plus');
      input.value = isPlus ? current + 1 : Math.max(1, current - 1);
    });
  })();

  // ============================================================
  // Product recommendations (async section)
  // ============================================================
  (function initProductRecs() {
    var section = qs('.equicore-product-recommendations[data-recommendations-url]');
    if (!section) return;
    var url = section.dataset.recommendationsUrl;
    if (!url) return;
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var inner = doc.querySelector('.equicore-product-recommendations');
        if (inner && inner.innerHTML.trim()) {
          section.innerHTML = inner.innerHTML;
        }
      })
      .catch(function () {});
  })();

  // ============================================================
  // Transparent header scroll behaviour
  // ============================================================
  (function initTransparentHeader() {
    var header = qs('.equicore-header--transparent');
    if (!header) return;
    function update() {
      if (window.scrollY > 60) {
        header.style.backgroundColor = 'var(--color-primary)';
        header.style.position = 'fixed';
        header.style.top = '0';
        header.style.left = '0';
        header.style.right = '0';
        header.style.zIndex = '200';
      } else {
        header.style.backgroundColor = '';
        header.style.position = '';
        header.style.top = '';
        header.style.left = '';
        header.style.right = '';
      }
    }
    on(window, 'scroll', update, { passive: true });
    update();
  })();

})();
